import { useEffect, useRef } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';

export function usePostHogIdentification() {
  const posthog = usePostHog();
  const { user, isImpersonating, originalUser } = useAuth();
  const { currentTenant } = useTenant();
  const prevUserRef = useRef<string | null>(null);
  const prevTenantRef = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog) return;

    const currentUserId = user?.id || null;
    const currentTenantId = currentTenant?.tenant_id || null;

    if (user && currentTenant) {
      // Create a unique distinct ID using user ID
      const distinctId = user.id;
      
      // Prepare person properties
      const personProperties: Record<string, any> = {
        email: user.email || '',
        user_id: user.id,
        tenant_id: currentTenant.tenant_id,
        tenant_name: currentTenant.company_name,
        user_created_at: user.created_at,
        last_sign_in_at: user.last_sign_in_at,
      };

      // Add impersonation context if applicable
      if (isImpersonating && originalUser) {
        personProperties.is_impersonating = true;
        personProperties.impersonated_user_email = user.email;
        personProperties.original_user_id = originalUser.id;
        personProperties.original_user_email = originalUser.email;
        
        // Track impersonation event
        if (prevUserRef.current !== currentUserId) {
          posthog.capture('User Impersonation Started', {
            impersonated_user_id: user.id,
            impersonated_user_email: user.email,
            original_user_id: originalUser.id,
            original_user_email: originalUser.email,
            tenant_id: currentTenant.tenant_id,
            tenant_name: currentTenant.company_name,
          });
        }
      } else {
        personProperties.is_impersonating = false;
        
        // Track regular login if this is a new user session
        if (prevUserRef.current !== currentUserId && prevUserRef.current !== null) {
          posthog.capture('User Login', {
            user_id: user.id,
            email: user.email,
            tenant_id: currentTenant.tenant_id,
            tenant_name: currentTenant.company_name,
          });
        }
      }

      // Track tenant switching
      if (prevTenantRef.current && prevTenantRef.current !== currentTenantId) {
        posthog.capture('Tenant Switch', {
          previous_tenant_id: prevTenantRef.current,
          new_tenant_id: currentTenant.tenant_id,
          new_tenant_name: currentTenant.company_name,
          user_id: user.id,
          email: user.email,
        });
      }

      // Add user metadata if available
      if (user.user_metadata) {
        if (user.user_metadata.full_name) {
          personProperties.name = user.user_metadata.full_name;
        }
        if (user.user_metadata.avatar_url) {
          personProperties.avatar_url = user.user_metadata.avatar_url;
        }
      }

      // Identify the user in PostHog
      posthog.identify(distinctId, personProperties);

      console.log('[PostHog] User identified:', {
        distinctId,
        email: user.email,
        tenant: currentTenant.company_name,
        isImpersonating
      });
    } else if (!user && prevUserRef.current) {
      // User logged out - track logout event first, then reset
      posthog.capture('User Logout', {
        previous_user_id: prevUserRef.current,
      });
      
      posthog.reset();
      console.log('[PostHog] User session reset');
    }

    // Update refs for next comparison
    prevUserRef.current = currentUserId;
    prevTenantRef.current = currentTenantId;
  }, [posthog, user, currentTenant, isImpersonating, originalUser]);

  return posthog;
}